import { Button, Grid, IconButton, Input, TextField, Box, CircularProgress, Typography, Alert, Stepper, Step, StepLabel } from "@mui/material";
import UploadIcon from '@mui/icons-material/Upload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CompareIcon from '@mui/icons-material/Compare';
import { useRef, useState } from "react";
import DocumentPreviewer from "./DocumentPreviewer";
import CustomDialog from "../../Shared/CustomDialog";
import { postData } from "../../../services/base.service";
import { CriteriaValidationResult, FormatedCV, StructuredCriteria } from "../../../types/dataTypes";
import StructuredCriteriaCards from "./StructuredCriteriaCard";
import ValidationResultsTable from "./ValidationResultTable";
import { removeFileExtension } from "../../../utils/utilFunctions";
import { useNotification } from "../../Shared/NotificationProvider";

// 🔥 NEW: Interface for tracking CV iterations
interface CVIteration {
  id: number;
  fileName: string;
  file: File;
  validationResults: CriteriaValidationResult[];
  formattedCV: FormatedCV;
  overallScore: number;
  timestamp: Date;
}

function CVEvaluatorView() {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const updatedCVInputRef = useRef<HTMLInputElement | null>(null);
    const [qualificationDialogueOpen, setQualificationDialogueOpen] = useState(false);
    const [qualificationTextOriginal, setQualificationTextOriginal] = useState<string>('');
    const [structuredCriteria, setStructuredCriteria] = useState<StructuredCriteria[]>([]);
    const [formattedCV, setFormattedCV] = useState<FormatedCV>();
    const [cvValidationResult, setCvValidationResult] = useState<CriteriaValidationResult[]>([]);
    
    // 🔥 NEW: CV iteration tracking
    const [cvIterations, setCvIterations] = useState<CVIteration[]>([]);
    const [currentIteration, setCurrentIteration] = useState<number>(0);
    const [showComparison, setShowComparison] = useState<boolean>(false);
    
    // Local loading states
    const [isProcessingCriteria, setIsProcessingCriteria] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isGeneratingCommentedCV, setIsGeneratingCommentedCV] = useState(false);
    const notify = useNotification();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            const ext = file.name.split('.').pop()?.toLowerCase();

            if (["pdf", "doc", "docx", "odt"].includes(ext || "")) {
                setSelectedFile(file);
            } else {
                notify('Invalid file type. Please select a PDF, DOC, DOCX, or ODT file.', 'warning');
            }
        }
    };

    // 🔥 NEW: Handle updated CV upload
    const handleUpdatedCVChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            const ext = file.name.split('.').pop()?.toLowerCase();

            if (["pdf", "doc", "docx", "odt"].includes(ext || "")) {
                setSelectedFile(file);
                // Automatically trigger validation for the updated CV
                setTimeout(() => handleValidation(), 100);
            } else {
                notify('Invalid file type. Please select a PDF, DOC, DOCX, or ODT file.', 'warning');
            }
        }
    };

    const handleQualificationTextInput = async () => {
        if (!qualificationTextOriginal.trim()) {
            notify('Please enter qualification criteria', 'warning');
            return;
        }
        
        setIsProcessingCriteria(true);

        try {
            const response = await postData('ai/structureCriteria', { criteria: qualificationTextOriginal });
            setStructuredCriteria(response as StructuredCriteria[]);
        } catch (error) {
            console.error('Error structuring criteria:', error);
            notify('Error processing criteria. Please try again.', 'error');
        } finally {
            setIsProcessingCriteria(false);
            setQualificationDialogueOpen(false);
        }
    };

    const handleValidation = async () => {
        setIsValidating(true);
        
        try {
            console.log('Validation triggered');
            
            // Create FormData
            const formData = new FormData();
            if (selectedFile)
                formData.append('cv', selectedFile);
            formData.append('structuredCriteria', JSON.stringify(structuredCriteria));
            
            // Send FormData directly
            const response = await postData('ai/validateCv', formData, 'json', undefined, false, 'none', '*/*');
            
            const validationResult = await response;
            const newValidationResults = validationResult.validation as CriteriaValidationResult[];
            const newFormattedCV = validationResult.formatedCv as FormatedCV;
            
            setCvValidationResult(newValidationResults);
            setFormattedCV(newFormattedCV);

            // 🔥 NEW: Calculate overall score and save iteration
            const overallScore = (newValidationResults.filter(result => result.requirementMet).length / newValidationResults.length) * 100;
            
            const newIteration: CVIteration = {
                id: cvIterations.length + 1,
                fileName: selectedFile?.name || `CV_v${cvIterations.length + 1}`,
                file: selectedFile!,
                validationResults: newValidationResults,
                formattedCV: newFormattedCV,
                overallScore,
                timestamp: new Date()
            };

            setCvIterations(prev => [...prev, newIteration]);
            setCurrentIteration(cvIterations.length);
            
            console.log('Validation completed');
        } catch (error) {
            console.error('Validation error:', error);
            notify('Error during validation. Please try again.', 'error');
        } finally {
            setIsValidating(false);
        }
    };

    const handleGenerateCommentedCV = async () => {
        setIsGeneratingCommentedCV(true);
        
        try {
            console.log('Generating commented CV...');
            
            const requestData = {
                formattedCV: formattedCV,
                structuredCriteria: structuredCriteria,
                originalFileName: removeFileExtension(selectedFile?.name || '')
            };
            
            // Call API to generate commented CV
            try {
            const response = await postData('ai/generateCommentedCv', {requestData: requestData}, 'blob');
            console.log(response);
            // Handle file download
            const url = window.URL.createObjectURL(response);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", selectedFile?.name ? `commented_${removeFileExtension(selectedFile.name)}` : `commented_CV.docx`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            } catch (err) {
            console.error(err);
            }
            
            console.log('Commented CV generated successfully');
            
        } catch (error) {
            console.error('Error generating commented CV:', error);
            notify('Error generating commented CV. Please try again.', 'error');
        } finally {
            setIsGeneratingCommentedCV(false);
        }
    };

    const triggerFileDialog = () => fileInputRef.current?.click();
    const triggerUpdatedCVDialog = () => updatedCVInputRef.current?.click();

    const canValidate = structuredCriteria.length > 0 && selectedFile !== null && !isValidating;
    const hasValidationResults = cvValidationResult.length > 0;
    const canGenerateCommentedCV = hasValidationResults && formattedCV && !isGeneratingCommentedCV;

    // 🔥 NEW: Get progress indicators
    const getScoreImprovement = () => {
        if (cvIterations.length < 2) return 0;
        const current = cvIterations[currentIteration]?.overallScore || 0;
        const previous = cvIterations[currentIteration - 1]?.overallScore || 0;
        return current - previous;
    };

    return (
        <Grid container spacing={2} sx={{ height: '100%', maxHeight: '100vh' }}>
            {/* 🔥 NEW: Workflow Progress Stepper */}
            {cvIterations.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                        <Stepper activeStep={Math.min(currentIteration + 1, 3)} alternativeLabel>
                            <Step>
                                <StepLabel>Initial Validation</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Download Commented CV</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Upload Improved CV</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Re-validation</StepLabel>
                            </Step>
                        </Stepper>
                    </Box>
                </Grid>
            )}

            {/* 🔥 NEW: Workflow Instructions */}
            {!hasValidationResults && (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Iterative CV Improvement Workflow</Typography>
                        <Typography variant="body2">
                            1. Upload your CV and job requirements for initial validation<br/>
                            2. Download the commented CV with AI-generated feedback wrapped in <strong>(- feedback -)</strong> brackets<br/>
                            3. Edit your CV based on the comments and upload the improved version<br/>
                            4. Repeat the process to continuously improve your CV's score
                        </Typography>
                    </Alert>
                </Grid>
            )}

            {/* Action Buttons - Fixed at top */}
            <Grid item xs={12}>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap: 2,
                        mt: 2,
                        mb: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    {/* Validate/Re-evaluate Button */}
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!canValidate}
                        onClick={handleValidation}
                        startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : 
                                  hasValidationResults ? <RefreshIcon /> : null}
                        sx={{
                            backgroundColor: canValidate ? '#4CAF50' : 'grey.400',
                            color: 'white',
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: canValidate ? '#45a049' : 'grey.400',
                            },
                            '&:disabled': {
                                backgroundColor: 'grey.400',
                                color: 'grey.600'
                            }
                        }}
                    >
                        {isValidating ? 'Validating... this may take up to 1 minute' : 
                         hasValidationResults ? `Re-evaluate (v${currentIteration + 2})` : 'Validate'}
                    </Button>

                    {/* 🔥 NEW: Download Commented CV Button */}
                    {hasValidationResults && (
                        <Button
                            variant="outlined"
                            size="large"
                            disabled={!canGenerateCommentedCV}
                            onClick={handleGenerateCommentedCV}
                            startIcon={isGeneratingCommentedCV ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                            sx={{
                                borderColor: canGenerateCommentedCV ? '#1976d2' : 'grey.400',
                                color: canGenerateCommentedCV ? '#1976d2' : 'grey.600',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: canGenerateCommentedCV ? '#1565c0' : 'grey.400',
                                    backgroundColor: canGenerateCommentedCV ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                },
                                '&:disabled': {
                                    borderColor: 'grey.400',
                                    color: 'grey.600'
                                }
                            }}
                        >
                            {isGeneratingCommentedCV ? 'Generating... this may take up to 1 minute' : 'Download Commented CV'}
                        </Button>
                    )}

                    {/* 🔥 NEW: Upload Improved CV Button */}
                    {hasValidationResults && (
                        <>
                            <Input
                                type="file"
                                sx={{ display: "none" }}
                                inputRef={updatedCVInputRef}
                                onChange={handleUpdatedCVChange}
                                inputProps={{ accept: ".pdf,.doc,.docx,.odt" }}
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={triggerUpdatedCVDialog}
                                startIcon={<EditIcon />}
                                sx={{
                                    backgroundColor: '#FF9800',
                                    color: 'white',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: '#F57C00',
                                    }
                                }}
                            >
                                Upload Improved CV
                            </Button>
                        </>
                    )}

                    {/* 🔥 NEW: Compare Versions Button */}
                    {cvIterations.length > 1 && (
                        <Button
                            variant="text"
                            size="large"
                            onClick={() => setShowComparison(!showComparison)}
                            startIcon={<CompareIcon />}
                            sx={{
                                color: '#7b1fa2',
                                px: 3,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {showComparison ? 'Hide Comparison' : 'Compare Versions'}
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* 🔥 NEW: Score Progress Indicator */}
            {cvIterations.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 3, 
                        p: 2, 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: 2,
                        mb: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Current Score: {cvIterations[currentIteration]?.overallScore.toFixed(1)}%
                        </Typography>
                        {cvIterations.length > 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ 
                                    color: getScoreImprovement() >= 0 ? '#4CAF50' : '#f44336',
                                    fontWeight: 'bold'
                                }}>
                                    {getScoreImprovement() >= 0 ? '+' : ''}{getScoreImprovement().toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    from v{currentIteration}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Version {currentIteration + 1} of {cvIterations.length}
                        </Typography>
                    </Box>
                </Grid>
            )}

            {/* Layout after validation: Full width validation results */}
            {hasValidationResults ? (
                <Grid item xs={12} sx={{ height: '100%', overflow: 'auto' }}>
                    <ValidationResultsTable 
                        validationResults={cvValidationResult}
                        title="CV Validation Results"
                        // 🔥 NEW: Pass comparison data
                        previousResults={showComparison && cvIterations.length > 1 ? cvIterations[currentIteration - 1]?.validationResults : undefined}
                        showComparison={showComparison}
                        iterationNumber={currentIteration + 1}
                    />
                </Grid>
            ) : (
                /* Layout before validation: Left = Job Requirements, Right = CV Upload */
                <>
                    {/* Left Side - Structured Criteria or Upload */}
                    <Grid item xs={6} sx={{ height: '100%', overflow: 'auto' }}>
                        {structuredCriteria.length > 0 ? (
                            <Box sx={{ pr: 2 }}>
                                <StructuredCriteriaCards 
                                    criteria={structuredCriteria}
                                    title="Job Requirements"
                                />
                            </Box>
                        ) : (
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    height: '200px'
                                }}
                            >
                                {isProcessingCriteria ? (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <CircularProgress />
                                        <Box sx={{ mt: 1 }}>Processing criteria...</Box>
                                    </Box>
                                ) : (
                                    <IconButton 
                                        onClick={() => setQualificationDialogueOpen(true)}
                                        sx={{
                                            flexDirection: 'column',
                                            gap: 1,
                                            padding: 3,
                                            borderRadius: 2,
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <UploadIcon sx={{ fontSize: 48 }} />
                                        Upload Qualifications
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Grid>

                    {/* Right Side - CV Upload/Preview */}
                    <Grid item xs={6} sx={{ height: '100%', overflow: 'auto' }}>
                        <Box sx={{ pl: 2, height: '100%' }}>
                            {selectedFile === null ? (
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        height: '200px'
                                    }}
                                >
                                    <Input
                                        type="file"
                                        sx={{ display: "none" }}
                                        inputRef={fileInputRef}
                                        onChange={handleFileChange}
                                        inputProps={{ accept: ".pdf,.doc,.docx,.odt" }}
                                    />
                                    <IconButton 
                                        onClick={triggerFileDialog}
                                        sx={{
                                            flexDirection: 'column',
                                            gap: 1,
                                            padding: 3,
                                            borderRadius: 2,
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <UploadFileIcon sx={{ fontSize: 48 }} />
                                        Upload CV
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <DocumentPreviewer file={selectedFile} />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </>
            )}

            {/* Qualification Input Dialog */}
            <CustomDialog
                open={qualificationDialogueOpen}
                onClose={() => setQualificationDialogueOpen(false)}
                aria-labelledby="add-expert-dialog"
                title='Please put in any qualification criteria. A certain structure is not required.'
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Qualifications"
                            multiline
                            value={qualificationTextOriginal}
                            onChange={(e) => setQualificationTextOriginal(e.target.value)}
                            rows={30}
                            disabled={isProcessingCriteria}
                        />
                    </Grid>
                    <Grid item>
                        <Button 
                            variant="contained" 
                            onClick={handleQualificationTextInput}
                            disabled={isProcessingCriteria}
                            startIcon={isProcessingCriteria ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isProcessingCriteria ? 'Processing...' : 'Ok'}
                        </Button>
                    </Grid>
                </Grid>
            </CustomDialog>
        </Grid>
    );
}

export default CVEvaluatorView;